// contexts/AnalysisContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useProgress } from './ProgressContext';
import { GameAnalysisService } from '../services/analysis';

const AnalysisContext = createContext(null);
const gameAnalysisService = new GameAnalysisService();

export const useAnalysis = () => {
    const context = useContext(AnalysisContext);
    if (!context) {
        throw new Error('useAnalysis must be used within an AnalysisProvider');
    }
    return context;
};

export const AnalysisProvider = ({ children }) => {
    const { startProgress, updateProgress, completeProgress } = useProgress();
    const [analysisState, setAnalysisState] = useState({
        isAnalyzing: false,
        results: null,
        error: null,
        partialResults: null
    });

    const analysisTimeout = useRef(null);
    const maxAnalysisTime = 300000; // 5 minutes

    const startAnalysis = useCallback(async (games) => {
        setAnalysisState({
            isAnalyzing: true,
            results: null,
            error: null,
            partialResults: null
        });

        startProgress(7, 'analysis');

        // Set analysis timeout
        const timeoutPromise = new Promise((_, reject) => {
            analysisTimeout.current = setTimeout(() => {
                reject(new Error('Analysis timeout - process took too long'));
            }, maxAnalysisTime);
        });

        try {
            const results = await Promise.race([
                gameAnalysisService.analyzeGames(
                    games,
                    (current, total, operation) => {
                        updateProgress(current, total, operation);
                    }
                ),
                timeoutPromise
            ]);

            setAnalysisState(prev => ({
                ...prev,
                isAnalyzing: false,
                results
            }));

            completeProgress();

        } catch (error) {
            console.error('Analysis failed:', error);
            setAnalysisState(prev => ({
                ...prev,
                isAnalyzing: false,
                error: error.message
            }));
        } finally {
            clearTimeout(analysisTimeout.current);
        }
    }, [startProgress, updateProgress, completeProgress]);

    const cancelAnalysis = useCallback(() => {
        gameAnalysisService.cancelAnalysis();
        clearTimeout(analysisTimeout.current);
        setAnalysisState(prev => ({
            ...prev,
            isAnalyzing: false,
            error: 'Analysis cancelled'
        }));
    }, []);

    const clearAnalysis = useCallback(() => {
        setAnalysisState({
            isAnalyzing: false,
            results: null,
            error: null,
            partialResults: null
        });
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearTimeout(analysisTimeout.current);
            gameAnalysisService.cancelAnalysis();
            clearAnalysis();
        };
    }, [clearAnalysis]);

    return (
        <AnalysisContext.Provider
            value={{
                ...analysisState,
                startAnalysis,
                cancelAnalysis,
                clearAnalysis
            }}
        >
            {children}
        </AnalysisContext.Provider>
    );
};